import { Link, Navigate, useParams } from "react-router";
import { ChevronRight } from "lucide-react";
import { getSolutionBySlug } from "../data/solutions";
import { SolutionSection } from "../components/SolutionSection";
import { MarketingBottomCta } from "../components/MarketingBottomCta";
import { Seo } from "../components/Seo";
import { JsonLd } from "../components/JsonLd";

const SITE_ORIGIN = "https://www.deskreplay.com";

export function SolutionTopicPage() {
  const { slug } = useParams();
  const sol = getSolutionBySlug(slug);

  if (!sol) {
    return <Navigate to="/solutions" replace />;
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_ORIGIN },
      { "@type": "ListItem", position: 2, name: "Solutions", item: `${SITE_ORIGIN}/solutions` },
      { "@type": "ListItem", position: 3, name: sol.pageTitle, item: `${SITE_ORIGIN}/solutions/${sol.id}` },
    ],
  };

  return (
    <div className="bg-slate-950 min-h-screen">
      <Seo
        title={sol.pageTitle}
        description={sol.metaDescription}
        canonicalPath={`/solutions/${sol.id}`}
      />
      <JsonLd schema={breadcrumbSchema} id="breadcrumb" />

      <section className="relative pt-36 pb-16 overflow-hidden border-b border-teal-500/10">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 70% 50% at 50% 0%, rgba(13, 148, 136, 0.15) 0%, transparent 60%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-xs text-on-dark-muted mb-8">
            <Link to="/" className="hover:text-teal-400 transition-colors">
              Home
            </Link>
            <span className="mx-2 text-slate-500">/</span>
            <Link to="/solutions" className="hover:text-teal-400 transition-colors">
              Solutions
            </Link>
            <span className="mx-2 text-slate-500">/</span>
            <span className="text-slate-400">{sol.tag}</span>
          </nav>
          <h1
            className="text-slate-100 mt-6 mb-4 max-w-4xl"
            style={{
              fontSize: "clamp(1.85rem, 4vw, 2.75rem)",
              fontWeight: 800,
              letterSpacing: "-0.03em",
              lineHeight: "1.15",
            }}
          >
            {sol.title}
          </h1>
          <p
            className={`text-lg max-w-3xl leading-relaxed mb-8 ${
              sol.accent === "teal" ? "text-teal-300" : "text-emerald-300"
            }`}
          >
            {sol.subtitle}
          </p>
          <Link
            to="/solutions"
            className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 text-sm transition-colors"
          >
            <ChevronRight size={16} className="rotate-180" />
            All solutions overview
          </Link>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SolutionSection sol={sol} index={0} hideTitle />
        </div>
      </section>

      <MarketingBottomCta
        titleBefore={sol.marketingCta.titleBefore}
        titleAccent={sol.marketingCta.titleAccent}
        description={sol.marketingCta.description}
      />
    </div>
  );
}
